import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { usePageMeta } from '@/hooks/usePageMeta';

const PORTRAIT =
  'https://customer-assets.emergentagent.com/job_gallerystore/artifacts/5rsgdx79_Graves%2C%20Joseph%2C%20AWsample2.JPG';

export const AboutPage = () => {
  usePageMeta({
    title: 'About — Graves Collective',
    description:
      'About the studio of Joseph Graves Jr. — painter, illustrator, and storyteller working from Colorado.',
    image: PORTRAIT,
  });

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="page-about">
      <SiteHeader />

      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-24 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden aspect-[4/5] bg-ink/[0.04]">
              <img
                src={PORTRAIT}
                alt="Joseph Graves Jr."
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">
              About the studio
            </p>
            <h1 className="font-display text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tight text-ink">
              Made by hand,
              <br />
              <span className="italic text-terracotta">told by heart.</span>
            </h1>

            <div className="mt-12 space-y-6 text-lg leading-[1.75] text-soft max-w-2xl">
              <p>
                Graves Collective is the studio shop of{' '}
                <span className="text-ink">Joseph Graves Jr.</span> — a painter,
                illustrator, and storyteller working from Colorado. The shop is
                where studio work meets the people who carry it home: prints,
                paintings, books, and wearable pieces, all made or finished by
                hand.
              </p>
              <p>
                Every print is signed. Every garment is built to last. Every
                book passes through the studio before it lands in your mailbox.
                Nothing here is mass-produced.
              </p>
              <p>
                If you’d like to commission a piece, ask about an original, or
                say hello, the studio is always open.
              </p>
            </div>

            <div className="mt-14 flex flex-wrap gap-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 text-sm uppercase tracking-[0.18em] bg-ink text-cream hover:bg-terracotta transition-colors group"
                data-testid="about-contact-cta"
              >
                Contact the studio
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="https://josephgravesjr.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group py-4"
                data-testid="about-artist-site-cta"
              >
                Visit the artist site
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Exhibitions & Placements */}
      <section
        className="border-t border-ink/10 bg-cream-deep/30"
        data-testid="about-exhibitions"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 lg:py-32">
          <div className="max-w-3xl mb-16">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">
              Exhibitions & Placements
            </p>
            <h2 className="font-display text-4xl lg:text-5xl text-ink tracking-tight leading-[1.05]">
              Where the work has been,{' '}
              <span className="italic text-terracotta">and where it&apos;s going.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
            {[
              { date: 'May 2026', title: 'Black Futures In Art', venue: 'Lafayette Arts & Cultural Resources · Lafayette, CO', kind: 'Group exhibition · Opening May 15' },
              { date: 'June 2026', title: 'Softpower / Hard Lines', venue: 'Tonantzin Casa de Café · 915 Santa Fe Dr, Denver', kind: 'Solo — First Friday, June 5' },
              { date: 'Jun–Aug 2026', title: 'Tilting West', venue: 'AC Galleries · Colorado', kind: 'Regional group show' },
              { date: 'Jul 2026', title: 'Boulder Pearl Street Arts Festival', venue: 'Pearl Street · Boulder, CO', kind: 'Festival · Meet the artist' },
              { date: 'Currently on view', title: 'Clayton Hotel', venue: 'Clayton Hotel & Members Club · Denver', kind: '"Mile High Maverick" & "Sunrise Ryder" · 48"×48"' },
              { date: 'Permanent collection', title: 'Central Denver Library', venue: 'Blair-Caldwell African American Research Library', kind: '"Cowboy Prince" — acquired for public collection' },
              { date: 'Nov 2025 – Jan 2026', title: 'In Plain Sight', venue: 'SeeSaw Gallery · Englewood, CO', kind: 'Solo · Reviewed in DARIA Magazine' },
              { date: 'Through Mar 2026', title: 'Black Futures in Art 2026', venue: 'Dairy Arts Center · McMahon Gallery · Boulder', kind: 'Group · Reviewed in Daily Camera' },
              { date: 'May–Jun 2025', title: 'Not Another Black Art Show', venue: 'Niza Knoll Gallery · 915 Santa Fe Dr, Denver', kind: 'Artist reception & panel' },
              { date: 'Feb–Mar 2025', title: 'BHM Group Show', venue: 'Charlie Cousins Gallery · Blair-Caldwell', kind: 'Black History Month group exhibition' },
              { date: 'Jan 2025', title: 'High Noon — Group Show', venue: 'Memento Mori Gallery · Lakewood, CO', kind: 'Group exhibition' },
            ].map((ex) => (
              <div key={ex.title} className="border-l-2 border-ink/10 pl-6 hover:border-terracotta transition-colors">
                <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-2">
                  {ex.date}
                </p>
                <h3 className="font-display text-2xl text-ink leading-tight mb-2">
                  {ex.title}
                </h3>
                <p className="text-sm text-soft leading-relaxed">
                  {ex.venue}
                </p>
                <p className="text-xs text-soft/80 mt-2 italic">{ex.kind}</p>
              </div>
            ))}
          </div>

          <p className="mt-16 text-sm text-soft">
            Full press &amp; exhibition history at{' '}
            <a
              href="https://josephgravesjr.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink hover:text-terracotta underline decoration-ink/20 hover:decoration-terracotta"
            >
              josephgravesjr.com
            </a>
            .
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AboutPage;
